import { useState, useEffect } from 'react';
import { getDetalleErroresTarifarios, createTarifariosMasivo } from '@shared/helpers/api/mantenedores/tarifarios';
import { getRegiones } from '@shared/helpers/api/mantenedores/generales';
import { getTiposVehiculo } from '@shared/helpers/api/mantenedores/vehiculos';
import { SuccessSwal, ErrorSwal } from '@shared/helpers/swal';

/**
 * Hook para manejar el detalle de errores de tarifarios
 */
export const useTarifariosErroresDetalleMantenedor = (user, permisos, tipoTarifarioId) => {
    const [detalleErrores, setDetalleErrores] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [savingPrecios, setSavingPrecios] = useState(false);

    // Filtros
    const [regionId, setRegionId] = useState('');
    const [regiones, setRegiones] = useState([]);
    
    // Tipos de vehículo
    const [tiposVehiculo, setTiposVehiculo] = useState([]);

    // Cargar regiones para filtro
    useEffect(() => {
        const loadRegiones = async () => {
            if (!user) return;

            try {
                const response = await getRegiones(user);
                setRegiones(response.success ? response.data : []);
            } catch (error) {
                console.error('Error al cargar regiones:', error);
                setRegiones([]);
            }
        };

        loadRegiones();
    }, [user]);

    // Cargar tipos de vehículo
    useEffect(() => {
        const loadTiposVehiculo = async () => {
            if (!user) return;

            try {
                const response = await getTiposVehiculo(user);
                setTiposVehiculo(response.success ? response.data : []);
            } catch (error) {
                console.error('Error al cargar tipos de vehículo:', error);
                setTiposVehiculo([]);
            }
        };

        loadTiposVehiculo();
    }, [user]);

    // Cargar detalle de errores
    useEffect(() => {
        const loadDetalleErrores = async () => {
            if (!user || !tipoTarifarioId) return;

            setLoading(true);
            setError(null);

            try {
                const params = {
                    region_id: regionId || undefined
                };

                const response = await getDetalleErroresTarifarios(user, tipoTarifarioId, params);

                if (response.success) {
                    setDetalleErrores(response.data);
                } else {
                    setDetalleErrores(null);
                    setError(response.error);
                }
            } catch (error) {
                console.error('Error al cargar detalle de errores:', error);
                setDetalleErrores(null);
                setError('Error al cargar detalle de errores');
            } finally {
                setLoading(false);
            }
        };

        loadDetalleErrores();
    }, [user, tipoTarifarioId, regionId]);

    const handleRegionChange = (value) => {
        setRegionId(value);
    };

    const handleSavePrecios = async (erroresConPrecios) => {
        if (!user || !erroresConPrecios || erroresConPrecios.length === 0) return;

        setSavingPrecios(true);

        try {
            // Preparar datos según el tipo de tarifario
            const tarifarios = erroresConPrecios.map(error => {
                const baseTarifario = {
                    precio: error.precio,
                    tipo_tarifario_id: error.tipo_tarifario_id,
                    comuna_id: error.comuna_id,
                    servicio_spread_id: error.servicio_spread_id,
                    tamano_spread_id: error.tamano_spread_id,
                    tms_id: error.tms_id
                };

                // Agregar centro_despacho_id si el servicio no es "RECOGIDO"
                if (error.servicio_nombre !== 'RECOGIDO') {
                    baseTarifario.centro_despacho_id = error.centro_despacho_id;
                }

                // Si es tipo "ingreso", agregar campos adicionales
                if (detalleErrores?.tipo_tarifario?.tipo === 'ingreso') {
                    baseTarifario.cliente_id = error.cliente_id;
                    baseTarifario.local_cliente_id = error.local_cliente_id;
                    baseTarifario.tamano_cliente_id = error.tamano_cliente_id;
                }

                // Si es tipo "egreso" y TMS es "GeoSort", agregar tipo_vehiculo_id y modalidad
                if (error.tms_nombre === 'GeoSort') {
                    baseTarifario.tipo_vehiculo_id = error.tipo_vehiculo_id;
                    baseTarifario.modalidad = error.modalidad;
                }

                return baseTarifario;
            });

            const response = await createTarifariosMasivo(user, tarifarios);

            if (response.success) {
                const { total_creados, total_errores } = response.data;
                SuccessSwal(`Tarifarios creados exitosamente. ${total_creados} creados${total_errores > 0 ? `, ${total_errores} errores` : ''}`);
                
                // Recargar datos
                const params = {
                    region_id: regionId || undefined
                };
                const nuevosErrores = await getDetalleErroresTarifarios(user, tipoTarifarioId, params);
                if (nuevosErrores.success) {
                    setDetalleErrores(nuevosErrores.data);
                }
            } else {
                ErrorSwal(response.error || 'Error al crear tarifarios');
            }
        } catch (error) {
            console.error('Error al guardar precios:', error);
            ErrorSwal('Error al guardar precios');
        } finally {
            setSavingPrecios(false);
        }
    };

    return {
        detalleErrores,
        loading,
        error,
        regionId,
        regiones,
        tiposVehiculo,
        handleRegionChange,
        handleSavePrecios,
        savingPrecios
    };
};
